import { Box, Button, Card, CardProps, Icon, Link, Text, useResponsive, useToast } from '@bitrise/bitkit';
import { ManualApprovalBuild } from '@/pages/App/AppDetails/components/BuildsSection/ManualApprovalBuildList/hooks/useManualApprovalBuildList';
import ColorBar from '@/components/ColorBar/ColorBar';
import BuildMetaData from '@/components/BuildListItem/BuildMetaData/BuildMetaData';
import { getGitProviderIconName } from '@/models/GitProvider';
import usePublicApiMutation from '@/hooks/api/usePublicApiMutation';
import { manualApprovalBuildPath } from '@/utils/publicApiRouteService';
import { SimpleErrorResponse } from '@/models/Error';
import AppForAppPage from '@/pages/App/types/AppForAppPage';

type ActionButtonsProps = {
  slug: string;
  app: AppForAppPage;
  onRefresh: () => void;
};

const ActionButtons = ({ slug, app, onRefresh }: ActionButtonsProps) => {
  const { isMobile } = useResponsive();

  const toast = useToast();

  const { mutate: updateManualApprovalBuild, isPending: isUpdateManualApprovalBuildLoading } = usePublicApiMutation<
    null,
    SimpleErrorResponse,
    { isApproved: boolean }
  >(manualApprovalBuildPath(app.slug, slug), 'patch', {
    onSuccess: () => {
      onRefresh();
    },
    onError: (error) =>
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Unknown error',
        status: 'error',
        isClosable: true,
      }),
  });

  return (
    <Box
      display="flex"
      flexDirection={['row-reverse', 'row']}
      flexWrap={['wrap', 'unset']}
      alignItems={['flex-start', 'center']}
      justifyContent="flex-end"
      gap="16"
      padding="12"
      width={isMobile ? '100%' : 'inherit'}
    >
      <Button
        variant="tertiary"
        size="sm"
        leftIconName="DeleteNope"
        color="red.50"
        onClick={() => updateManualApprovalBuild({ isApproved: false })}
        isDisabled={isUpdateManualApprovalBuildLoading}
      >
        Decline
      </Button>
      <Button
        size="sm"
        leftIconName="Check"
        onClick={() => updateManualApprovalBuild({ isApproved: true })}
        isDisabled={isUpdateManualApprovalBuildLoading}
      >
        Approve and run {isMobile ? '' : 'Build'}
      </Button>
    </Box>
  );
};

type ManualApprovalBuildListItemProps = {
  app: AppForAppPage;
  onRefresh: () => void;
  manualApprovalBuildItem: ManualApprovalBuild;
} & CardProps;

const ManualApprovalBuildListItem = ({
  app,
  onRefresh,
  manualApprovalBuildItem: { slug, buildParams },
  ...rest
}: ManualApprovalBuildListItemProps): JSX.Element => {
  const { isMobile } = useResponsive();

  return (
    <Card as="div" overflow="hidden" role="group" withHover={false} display="flex" borderRadius="8" {...rest}>
      <ColorBar color="sys/info/base" />
      <Box position="relative" flexGrow={1} flexShrink={1} minWidth="0">
        <Box
          display="flex"
          flexDirection={['column', 'row']}
          alignItems="center"
          justifyContent={['flex-end', 'center']}
          width="100%"
          borderBottom="1px solid"
          borderBottomColor="neutral.93"
        >
          <Box
            display="flex"
            flexDirection="row"
            gap="12"
            padding="12"
            alignItems="center"
            overflow="hidden"
            width="100%"
          >
            <Icon name="Clock" size="24" textColor="blue.50" />
            <Box display="flex" flexDirection="column" overflow="hidden" flexGrow={1}>
              <Text hasEllipsis fontSize="16" lineHeight="24px" fontWeight="bold">
                PR is waiting for approval
              </Text>
              {!isMobile && (
                <Text hasEllipsis fontSize="16" lineHeight="20px" maxWidth="720px">
                  {buildParams.commitMessage}
                </Text>
              )}
            </Box>
          </Box>
          {isMobile && (
            <Box display="flex" width="100%" padding="12" borderTop="1px solid" borderTopColor="neutral.93">
              <Text hasEllipsis fontSize="16" lineHeight="20px" maxWidth="720px">
                {buildParams.commitMessage}
              </Text>
            </Box>
          )}
          {!isMobile && <ActionButtons slug={slug} app={app} onRefresh={onRefresh} />}
        </Box>
        <Box
          display="flex"
          justifyContent={['space-between', 'flex-start']}
          alignItems="center"
          gap="4"
          paddingX="12"
          paddingTop="8"
          paddingBottom="10"
          borderBottom={isMobile ? '1px solid' : 'none'}
          borderBottomColor={isMobile ? 'neutral.93' : 'none'}
        >
          <BuildMetaData icon="Trigger" color="neutral.40" textColor="neutral.40" lineHeight="20" flexShrink={0}>
            {buildParams.pullRequestAuthor}
          </BuildMetaData>
          <Icon name="Bulletpoint" size="16" color="neutral.70" />
          {buildParams.pullRequestRepositoryUrl && (
            <BuildMetaData
              icon={getGitProviderIconName(app.provider)}
              color="purple.50"
              textColor="purple.50"
              flexShrink={0}
            >
              <Link
                href={buildParams.pullRequestRepositoryUrl}
                size="2"
                isExternal
                whiteSpace="nowrap"
                colorScheme="purple"
                lineHeight="20px"
              >
                Review PR
              </Link>
            </BuildMetaData>
          )}
        </Box>
        {isMobile && <ActionButtons slug={slug} app={app} onRefresh={onRefresh} />}
      </Box>
    </Card>
  );
};

export default ManualApprovalBuildListItem;
